import { Box, CircularProgress, InputBase, Typography } from '@mui/material';
import { useInfiniteQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { useDebouncedValue } from '../../hooks/useDebouncedValue';
import { useStatuteApi } from '../../services/api/useStatute.api';
import { ApiResponse } from '../../types/ApiResponse.type';
import { GetStatutesQueryParams } from '../../types/QueryParams.type';
import { StatuteKind } from '../../types/SimilarlySituated/StatuteKind.type';
import { Statute } from '../../types/Statute.type';
import { normalizeText } from '../../utils/normalizeText.ts';
import { BUTTON_RESET } from '../../utils/ssbStyles';
import RemovableChip from './RemovableChip';

const KIND_FILTERS: Record<StatuteKind, Pick<GetStatutesQueryParams, 'is_enhancement' | 'is_wobbler'>> = {
  charge: { is_enhancement: false },
  enhancement: { is_enhancement: true },
  wobbler: { is_enhancement: false, is_wobbler: true },
};

interface StatuteSelectorTrayProps {
  statutes: Statute[];
  countyId?: number | null;
  kind: StatuteKind;
  onChange: (statutes: Statute[]) => void;
  showSelected?: boolean;
  initiallyOpen?: boolean;
  closeOnSelect?: boolean;
}

function StatuteSelectorTray({
  statutes,
  countyId,
  kind,
  onChange,
  showSelected = true,
  initiallyOpen = false,
  closeOnSelect = false,
}: StatuteSelectorTrayProps) {
  const { getStatutes } = useStatuteApi();
  const [searchInput, setSearchInput] = useState('');
  const searchQuery = useDebouncedValue(searchInput, 300);
  const [isListOpen, setIsListOpen] = useState(initiallyOpen);

  const { data, fetchNextPage, hasNextPage, isFetchingNextPage, isLoading } = useInfiniteQuery({
    queryKey: ['ssbStatutePicker', countyId, kind, searchQuery],
    queryFn: ({ pageParam = 1 }) =>
      getStatutes({
        page: pageParam,
        per_page: 100,
        county_id: countyId || undefined,
        query: searchQuery,
        ...KIND_FILTERS[kind],
      }),
    initialPageParam: 1,
    getNextPageParam: (lastPage: ApiResponse<Statute[]>) => {
      const page = lastPage?.meta.pagination?.page ?? 0;
      const pages = lastPage?.meta.pagination?.pages ?? 0;
      return page < pages ? page + 1 : undefined;
    },
    enabled: isListOpen,
  });

  const options = (data?.pages.flatMap((page) => page.data) ?? []).filter(
    (option) => !statutes.some((selected) => selected.id === option.id),
  );

  const addStatute = (statute: Statute) => {
    onChange([...statutes, statute]);
    setSearchInput('');
    if (closeOnSelect) setIsListOpen(false);
  };

  return (
    <Box>
      <Box
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
          alignItems: 'center',
          gap: '8px',
          border: '1px solid #CBD5E0',
          borderRadius: '8px',
          backgroundColor: '#fff',
          padding: '8px 10px',
        }}
      >
        {showSelected &&
          statutes.map((statute, index) => (
            <Box key={statute.id} sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              {index > 0 && <Typography sx={{ fontSize: '11px', fontWeight: 600, color: '#718096' }}>OR</Typography>}
              <RemovableChip
                variant='neutral'
                label={statute.display_name}
                description={normalizeText(statute.description)}
                onRemove={() => onChange(statutes.filter((s) => s.id !== statute.id))}
              />
            </Box>
          ))}
        {showSelected && statutes.length > 0 && (
          <Typography sx={{ fontSize: '11px', fontWeight: 600, color: '#718096' }}>OR</Typography>
        )}
        <InputBase
          value={searchInput}
          onChange={(event) => setSearchInput(event.target.value)}
          onFocus={() => setIsListOpen(true)}
          placeholder={'Search statute or code…'}
          sx={{
            flexGrow: 1,
            minWidth: 120,
            fontSize: '13px',
            border: '1px dashed #CBD5E0',
            borderRadius: '6px',
            padding: '2px 8px',
          }}
        />
        {isListOpen && !closeOnSelect && (
          <Box
            component='button'
            type='button'
            onClick={() => {
              setIsListOpen(false);
              setSearchInput('');
            }}
            sx={{
              ...BUTTON_RESET,
              padding: '2px 6px',
              fontSize: '12px',
              color: '#1D4ED8',
              cursor: 'pointer',
              fontWeight: 500,
              transition: 'color 0.2s ease-in-out',
              ':hover': { color: 'primary.main' },
            }}
          >
            Done
          </Box>
        )}
      </Box>

      {isListOpen && (
        <Box
          onScroll={(event) => {
            const node = event.currentTarget;
            if (node.scrollHeight - node.scrollTop - node.clientHeight <= 1 && hasNextPage && !isFetchingNextPage) {
              fetchNextPage();
            }
          }}
          sx={{
            marginTop: '8px',
            border: '1px solid #E2E8F0',
            borderRadius: '8px',
            backgroundColor: '#fff',
            maxHeight: 260,
            overflowY: 'auto',
          }}
        >
          {isLoading && (
            <Box sx={{ display: 'flex', justifyContent: 'center', padding: '14px' }}>
              <CircularProgress size={20} />
            </Box>
          )}
          {!isLoading && options.length === 0 && (
            <Typography sx={{ fontSize: '13px', color: '#718096', padding: '12px' }}>No statutes found</Typography>
          )}
          {!isLoading &&
            options.map((statute) => (
              <Box
                key={statute.id}
                component='button'
                type='button'
                onClick={() => addStatute(statute)}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px',
                  width: '100%',
                  border: 'none',
                  borderBottom: '1px solid #EDF2F7',
                  backgroundColor: 'transparent',
                  padding: '8px 12px',
                  textAlign: 'left',
                  cursor: 'pointer',
                  '&:hover': { backgroundColor: '#EBF8FF' },
                }}
              >
                <Typography sx={{ fontSize: '13px', fontWeight: 600, width: 130, flexShrink: 0 }}>
                  {statute.display_name}
                </Typography>
                <Typography
                  sx={{
                    fontSize: '12px',
                    color: '#4A5568',
                    flexGrow: 1,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {normalizeText(statute.description)}
                </Typography>
              </Box>
            ))}
          {isFetchingNextPage && (
            <Box sx={{ display: 'flex', justifyContent: 'center', padding: '8px' }}>
              <CircularProgress size={18} />
            </Box>
          )}
        </Box>
      )}
    </Box>
  );
}

export default StatuteSelectorTray;
